import type { NodeStyle, ViewElementState } from "@ae/domain";
/**
 * Top-of-canvas toolbar with two sections:
 *
 * 1. Text & fill formatting — bold, italic, underline, font size, font family,
 *    font colour, fill colour. Active when ≥ 1 node is selected; applies the
 *    patch to every selected instance via setNodeStyleOnCanvas (each call
 *    records its own undo entry).
 *
 * 2. Alignment — left, centre-X, right, top, centre-Y, bottom edges. Active
 *    when ≥ 2 nodes are selected; computes absolute positions from viewState
 *    and calls moveConceptOnCanvas.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../../state/session";
import { type CapturedFormat, useUi } from "../../state/ui";

// Patch type that allows explicit undefined to clear individual style fields.
type NodeStylePatch = { [K in keyof NodeStyle]?: NodeStyle[K] | undefined };

import type { Node } from "@xyflow/react";
import { vsGet } from "../../state/viewStateAccess";
import { useWorkspaceData } from "../../state/workspace";
import { angleFromPoint, pointFromAngle } from "./angleUtils";
import { RibbonBar, RibbonGroup } from "./RibbonLayout";
import { SwatchColorPicker } from "./SwatchColorPicker";

interface ToolbarProps {
  workspaceId: string | null;
  branchId: string | null;
  nodes: Node[];
  /** Single most-recently-selected edge — used by per-edge actions
   *  (orthogonalize) that don't make sense in bulk. */
  selectedRelationId?: string | null;
  /** All currently-selected edges — colour and routing actions apply
   *  to this whole set. Empty array when nothing is selected. */
  selectedRelationIds?: string[];
  /**
   * De entry-sleutels van de geselecteerde VOORKOMENS. Alles wat in de
   * viewState van één voorkomen leeft (lijnkleur, lijnstijl, routing,
   * label-opmaak) hoort hierop te werken en niet op de relatie als geheel.
   */
  selectedEdgeKeys?: string[];
  /**
   * Wat dit canvas niet kan. De werkbalk blijft op elk diagramtype dezelfde —
   * wat hier niets uitricht staat zichtbaar uitgeschakeld in plaats van
   * stilletjes te verdwijnen, zodat de plek van een knop niet verspringt.
   *
   * Het banen-canvas kent bijvoorbeeld geen z-volgorde (kaarten overlappen
   * niet), tekent z'n stromen zelf (dus geen lijnstijl of routing) en heeft
   * geen icoon-in-de-hoek. Een baankop heeft bovendien een vaste maat.
   */
  nietBeschikbaar?: {
    uitlijnen?: boolean;
    zOrder?: boolean;
    lijnen?: boolean;
    overlay?: boolean;
    iconen?: boolean;
    maat?: boolean;
  };
}

// ── Orthogonalization helpers ──────────────────────────────────────────────

interface Pt {
  x: number;
  y: number;
}

/**
 * Snap intermediate waypoints zodat elk segment perfect horizontaal of
 * verticaal wordt, met source/target endpoints (p0, pn) als vaste
 * "leidende" punten (Gerald-spec 2026-05-27: "begin en eind coordinaat
 * leidend").
 *
 * Voor elk segment:
 *  - Classificeer als V (verticaal) of H (horizontaal) op basis van
 *    de ORIGINELE punten (zodat eerdere snap-stappen latere segmenten
 *    niet herclassificeren)
 *  - Niet-laatste segment: snap end-point naar START-point's coord
 *    (cascadeert vanuit p0 vooruit door de lijn)
 *  - Laatste segment: snap start-point naar END-point's coord (= pn)
 *    zodat de overgang naar de target ook orthogonaal landt
 *
 * Resultaat: een lijst met dezelfde lengte als input-waypoints, alleen
 * dan met X- of Y-waarden aangepast zodat alle secties recht zijn.
 */
function orthogonalizeWaypoints(p0: Pt, waypoints: Pt[], pn: Pt): Pt[] {
  if (waypoints.length === 0) return [];

  // Special case 1-waypoint: een lijn met één tussenpunt vormt altijd
  // een L-bend (geen Z mogelijk met enkel 3 punten). Per-segment
  // classificatie kan beide segmenten dezelfde as toewijzen, wat
  // conflict geeft (waypoint.y moet zowel p0.y als pn.y zijn).
  // Daarom expliciet kiezen tussen de twee mogelijke L-bends:
  //   A) waypoint = (p0.x, pn.y)  — eerst verticaal, dan horizontaal
  //   B) waypoint = (pn.x, p0.y)  — eerst horizontaal, dan verticaal
  // De keuze valt op die het dichtst ligt bij de huidige waypoint-
  // positie (respecteert gebruiker-intentie zoals "ik wilde 'm daar").
  if (waypoints.length === 1) {
    const w = waypoints[0]!;
    const optionA = { x: p0.x, y: pn.y };
    const optionB = { x: pn.x, y: p0.y };
    const distA = (w.x - optionA.x) ** 2 + (w.y - optionA.y) ** 2;
    const distB = (w.x - optionB.x) ** 2 + (w.y - optionB.y) ** 2;
    return [distA <= distB ? optionA : optionB];
  }

  const all: Pt[] = [p0, ...waypoints.map((p) => ({ ...p })), pn];
  const n = all.length - 1; // aantal segmenten

  const orig = all.map((p) => ({ ...p }));
  const isV = (a: Pt, b: Pt) => Math.abs(b.y - a.y) > Math.abs(b.x - a.x);

  for (let i = 0; i < n; i++) {
    const vertical = isV(orig[i]!, orig[i + 1]!);
    const isLast = i === n - 1;
    if (vertical) {
      const x = isLast ? pn.x : all[i]!.x;
      if (i > 0) all[i]!.x = x;
      if (i + 1 < n) all[i + 1]!.x = x;
    } else {
      const y = isLast ? pn.y : all[i]!.y;
      if (i > 0) all[i]!.y = y;
      if (i + 1 < n) all[i + 1]!.y = y;
    }
  }
  return all.slice(1, n) as Pt[];
}

/**
 * Resolve a node's absolute box (top-left x/y + width/height) from
 * viewState, walking up `containerId` so a nested child's box gets the
 * parent offsets folded in. Same logic the edge-renderer uses, returned
 * as a plain rect for the orthogonalize paths that need to test
 * "do these two boxes overlap on X / Y?".
 */
function nodeAbsoluteRect(
  viewState: Record<string, ViewElementState>,
  nodeId: string,
): { x: number; y: number; w: number; h: number } | null {
  const entry = viewState[nodeId];
  if (!entry?.position) return null;
  let x = entry.position.x;
  let y = entry.position.y;
  let cur = entry;
  while (cur.containerId) {
    const parent = viewState[cur.containerId];
    if (!parent?.position) break;
    x += parent.position.x;
    y += parent.position.y;
    cur = parent;
  }
  // Junction-correction (2026-05-27): zelfde force-22×22-logica als
  // in `collectItems`. AND/OR-junctions renderen visueel als 22×22
  // stip met center op (position + 11), ongeacht viewState.width/
  // height of DOM-rect-grootte. Voor consistente Recht-berekening
  // moeten we DEZELFDE rect gebruiken die align gebruikt — anders
  // landt het Recht-resultaat op een Y die niet matcht met de Y
  // waarop align de junction centreerde. Zie bug-conversation
  // 2026-05-27: 9×9-viewState-junction kreeg orthogonalize-center
  // op (position+4.5), maar align-center op (position+11) →
  // 6.5 px misalignment.
  const refId = entry.ref ?? nodeId;
  const concept = useWorkspaceData.getState().store.getConcept(refId);
  const isJunction =
    concept?.typeId === "archimate.AndJunction" || concept?.typeId === "archimate.OrJunction";
  const dom = readNodeSize(nodeId);
  // ... maar niet op het banen-canvas. Daar IS het vak het symbool: de stip
  // staat in het midden van een kaart van 30 bij 30, en 22 forceren schuift
  // 'm vier punten omlaag — precies wat een lijn die recht had moeten lopen
  // scheef trekt (gemeten 2026-09-17).
  if (isJunction && !dom?.banenKaart) {
    return { x, y, w: 22, h: 22 };
  }
  const w = dom?.width ?? entry.width ?? 140;
  const h = dom?.height ?? entry.height ?? 60;
  return { x, y, w, h };
}

type AlignAction = "left" | "right" | "top" | "bottom" | "center-x" | "center-y";

interface Item {
  id: string;
  absX: number;
  absY: number;
  width: number;
  height: number;
  parentAbs: { x: number; y: number } | null;
}

function readNodeSize(
  nodeId: string,
): { width: number; height: number; banenKaart: boolean } | null {
  if (typeof document === "undefined") return null;
  const safe = nodeId.replace(/(["'\\])/g, "\\$1");
  // Twee canvassen, twee soorten elementen: React Flow hangt z'n `data-id` op
  // de node-wrapper, het banen-canvas z'n `data-stap-id` op de kaart. Zonder
  // die tweede vond de werkbalk daar niets en viel 'ie terug op 140x60 — dan
  // lijken alle kaarten even groot en doet "gelijke maat" niets.
  const rf = document.querySelector(`.react-flow__node[data-id="${safe}"]`) as HTMLElement | null;
  const el = rf ?? (document.querySelector(`[data-stap-id="${safe}"]`) as HTMLElement | null);
  if (!el) return null;
  // Welk canvas 'm leverde doet ertoe voor een junction: zie de twee plekken
  // waar de maat op 22x22 wordt gezet.
  return { width: el.offsetWidth, height: el.offsetHeight, banenKaart: !rf };
}

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48];
const FONT_FAMILIES = [
  { label: "Standaard", value: "" },
  { label: "Sans-serif", value: "sans-serif" },
  { label: "Serif", value: "serif" },
  { label: "Monospace", value: "monospace" },
  { label: "Arial", value: "Arial, sans-serif" },
  { label: "Verdana", value: "Verdana, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Courier New", value: "'Courier New', monospace" },
];

/**
 * Inwaarts wijzende dubbelpijl: arrows-meeting-in-the-middle, classic
 * "centreer in deze richting" icon. Used for horizontal and vertical
 * alignment buttons in the Uitlijnen group.
 */
function HCenterIcon() {
  return (
    <svg viewBox="0 0 16 14" width="14" height="12" fill="currentColor" aria-hidden>
      <rect x="2" y="6" width="3" height="2" />
      <polygon points="5,4 5,10 8,7" />
      <polygon points="11,4 11,10 8,7" />
      <rect x="11" y="6" width="3" height="2" />
    </svg>
  );
}
function VCenterIcon() {
  return (
    <svg viewBox="0 0 14 16" width="12" height="14" fill="currentColor" aria-hidden>
      <rect x="6" y="2" width="2" height="3" />
      <polygon points="3,5 11,5 7,8" />
      <polygon points="3,11 11,11 7,8" />
      <rect x="6" y="11" width="2" height="3" />
    </svg>
  );
}

/**
 * Tekst-uitlijn-icoon (links/midden/rechts): vier regels, de korte regels
 * verschoven naar de gekozen kant. Font-onafhankelijk (verving de Unicode-
 * glyphs ⇤/⇔/⇥ die op sommige systemen als tofu-blokjes renderden).
 */
function TextAlignIcon({ align }: { align: "left" | "center" | "right" }) {
  const short =
    align === "left"
      ? { x1: 2, x2: 10 }
      : align === "right"
        ? { x1: 6, x2: 14 }
        : { x1: 4, x2: 12 };
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      fill="none"
      aria-hidden
    >
      <line x1="2" y1="3.5" x2="14" y2="3.5" />
      <line x1={short.x1} y1="7" x2={short.x2} y2="7" />
      <line x1="2" y1="10.5" x2="14" y2="10.5" />
      <line x1={short.x1} y1="14" x2={short.x2} y2="14" />
    </svg>
  );
}

/**
 * Richtingspijl voor de "Tekst in object"-positiegrid. Eén opwaartse pijl,
 * geroteerd per richting (verving de Unicode-pijlen ↖↑↗←→↙↓↘).
 */
function PositionArrow({ deg }: { deg: number }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ transform: `rotate(${deg}deg)` }}
    >
      <line x1="8" y1="13" x2="8" y2="3" />
      <polyline points="4,7 8,3 12,7" />
    </svg>
  );
}

/**
 * Pijl-naar-rand (object-uitlijnen + naar-voor/achter): een pijl die naar een
 * dwarsbalk wijst. Geroteerd per richting (verving ⇤⇥⤒⤓). 0° = omhoog.
 */
function EdgeArrow({ deg }: { deg: number }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ transform: `rotate(${deg}deg)` }}
    >
      <line x1="3" y1="3" x2="13" y2="3" />
      <line x1="8" y1="13" x2="8" y2="6" />
      <polyline points="5,9 8,6 11,9" />
    </svg>
  );
}

/** Hoek/richting → rotatiehoek voor PositionArrow (0° = omhoog). */
const POSITION_ARROW_DEG: Record<string, number> = {
  "center-top": 0,
  "right-top": 45,
  "right-middle": 90,
  "right-bottom": 135,
  "center-bottom": 180,
  "left-bottom": 225,
  "left-middle": 270,
  "left-top": 315,
};

export function CanvasToolbar({
  workspaceId,
  branchId,
  nodes,
  selectedRelationId,
  selectedRelationIds: selectedRelationIdsProp,
  selectedEdgeKeys: selectedEdgeKeysProp,
  nietBeschikbaar,
}: ToolbarProps) {
  // Alleen kijken: alleen de hulp blijft; opmaak, schikken, overlay en
  // vereenvoudigen schrijven allemaal naar het diagram.
  const readOnly = useSession((s) => s.readOnly);
  // Normalise to a non-empty array when at least one edge is selected.
  // Falls back to the singular `selectedRelationId` if the parent
  // forgot to pass the array (defensive — parents shipping today pass
  // the array form).
  const selectedRelationIds: string[] = (() => {
    if (selectedRelationIdsProp && selectedRelationIdsProp.length > 0) {
      return selectedRelationIdsProp;
    }
    if (selectedRelationId) return [selectedRelationId];
    return [];
  })();
  /**
   * De voorkomens waarop een opmaak-bewerking landt. Valt terug op de
   * relatie-ids wanneer een aanroeper de sleutels niet meestuurt — dan geldt
   * het oude gedrag (het eerste voorkomen van elke relatie).
   */
  const selectedEdgeKeys: string[] =
    selectedEdgeKeysProp && selectedEdgeKeysProp.length > 0
      ? selectedEdgeKeysProp
      : selectedRelationIds;
  const hasEdgeSelection = selectedRelationIds.length > 0;
  const multiEdgeSelection = selectedRelationIds.length > 1;
  const { t } = useTranslation();
  const overlayPanelOpen = useUi((s) => s.overlayPanelOpen);
  const setOverlayPanelOpen = useUi((s) => s.setOverlayPanelOpen);
  const simplifyPanelOpen = useUi((s) => s.simplifyPanelOpen);
  const setSimplifyPanelOpen = useUi((s) => s.setSimplifyPanelOpen);
  /**
   * Het object waarvan de rest z'n eigenschap overneemt: het eerst
   * aangeklikte. `null` na een sleepselectie — dan bestaat er geen
   * klikvolgorde en valt elke bewerking terug op z'n oude regel.
   */
  const ankerConceptId = useUi((s) => s.selectionAnchorConceptId);
  const selected = nodes.filter((n) => n.selected);

  /** Het voorkomen van het anker op dít diagram, of `undefined`. */
  const ankerNodeId = (() => {
    if (!ankerConceptId) return undefined;
    const ws = useWorkspaceData.getState();
    const diagram = ws.store.getDiagram(ws.activeDiagramId ?? "");
    if (!diagram) return undefined;
    return selected.find((n) => (diagram.viewState[n.id]?.ref ?? n.id) === ankerConceptId)?.id;
  })();
  const hasSelection = selected.length >= 1 && !!workspaceId && !!branchId;
  const canAlign =
    !nietBeschikbaar?.uitlijnen && selected.length >= 2 && !!workspaceId && !!branchId;
  /** Wat de knoppen per groep aan mogen — zie `nietBeschikbaar`. */
  const magZOrder = hasSelection && !nietBeschikbaar?.zOrder;
  const magLijnen = !nietBeschikbaar?.lijnen;
  const magIconen = hasSelection && !nietBeschikbaar?.iconen;
  const magMaat = hasSelection && !nietBeschikbaar?.maat;

  const firstSelectedEdgeId = selectedEdgeKeys[0];

  // Read the nodeStyle of the first selected node to reflect current state in
  // the controls. For multi-selection we show the first node's values only.
  const firstStyle: NodeStyle = (() => {
    const ws = useWorkspaceData.getState();
    const diagramId = ws.activeDiagramId;
    if (!diagramId) return {};
    const diagram = ws.store.getDiagram(diagramId);
    if (!diagram) return {};
    // De werkbalk toont de opmaak van het anker — hetzelfde object dat de
    // maatstaf is voor gelijke maat en uitlijnen. Zonder anker de eerste.
    const first = selected.find((n) => n.id === ankerNodeId) ?? selected[0];
    if (first) return diagram.viewState[first.id]?.nodeStyle ?? {};
    // Geen node maar wel een relatie geselecteerd: toon de opmaak van het
    // edge-label, zodat de velden in de werkbalk laten zien wat er staat.
    if (!firstSelectedEdgeId) return {};
    const ve = vsGet(diagram.viewState, firstSelectedEdgeId);
    if (!ve) return {};
    return {
      ...(ve.labelFontSize !== undefined ? { fontSize: ve.labelFontSize } : {}),
      ...(ve.labelBold !== undefined ? { bold: ve.labelBold } : {}),
      ...(ve.labelItalic !== undefined ? { italic: ve.labelItalic } : {}),
      ...(ve.labelUnderline !== undefined ? { underline: ve.labelUnderline } : {}),
      ...(ve.labelFontFamily !== undefined ? { fontFamily: ve.labelFontFamily } : {}),
    };
  })();

  // Apply a style patch to every selected node — én aan het label van elke
  // geselecteerde relatie. Bij een relatie hangt de opmaak aan het label, dus
  // dezelfde vier knoppen sturen daar de `label*`-velden aan.
  const applyStyle = (patch: NodeStylePatch) => {
    if (!workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    for (const n of selected) {
      ws.setNodeStyleOnCanvas(workspaceId, branchId, n.id, patch as Partial<NodeStyle>);
    }
    if (selectedRelationIds.length === 0) return;
    const naarLabel = {
      ...("fontSize" in patch ? { labelFontSize: patch.fontSize ?? null } : {}),
      ...("bold" in patch ? { labelBold: patch.bold ?? null } : {}),
      ...("italic" in patch ? { labelItalic: patch.italic ?? null } : {}),
      ...("underline" in patch ? { labelUnderline: patch.underline ?? null } : {}),
      ...("fontFamily" in patch ? { labelFontFamily: patch.fontFamily ?? null } : {}),
    };
    if (Object.keys(naarLabel).length === 0) return;
    for (const id of selectedEdgeKeys) {
      ws.setRelationLabelStyleOnCanvas(workspaceId, branchId, id, naarLabel);
    }
  };

  // Toggle a boolean style property (bold / italic / underline).
  const toggle = (key: keyof NodeStyle) => {
    applyStyle({ [key]: !firstStyle[key] } as NodeStylePatch);
  };

  // ── Alignment ────────────────────────────────────────────────────────────

  const collectItems = (): Item[] => {
    const ws = useWorkspaceData.getState();
    const diagramId = ws.activeDiagramId;
    if (!diagramId) return [];
    const diagram = ws.store.getDiagram(diagramId);
    if (!diagram) return [];

    const absOf = (key: string): { x: number; y: number } | null => {
      const ve = diagram.viewState[key];
      if (!ve?.position) return null;
      let x = ve.position.x;
      let y = ve.position.y;
      let cur = ve;
      while (cur.containerId) {
        const parent: typeof cur | undefined = diagram.viewState[cur.containerId];
        if (!parent?.position) break;
        x += parent.position.x;
        y += parent.position.y;
        cur = parent;
      }
      return { x, y };
    };

    const items: Item[] = [];
    for (const n of selected) {
      const abs = absOf(n.id);
      if (!abs) continue;
      const ve = diagram.viewState[n.id];
      // Junction-correction (2026-05-26): AND/OR-junctions renderen
      // visueel als 22×22 stip rechtsboven-aan in een potentieel grotere
      // node-rect (oude entries kregen default 140×60 of een resize-
      // restant). De stip zit visueel op (position.x + 11, position.y
      // + 11). Voor alignment willen we DIE coördinaat als centrum
      // gebruiken — anders berekent de helper het centrum t.o.v. de
      // grotere DOM-rect en schiet de partner-box LINKS van de
      // werkelijke stip. Door width/height op 22 te forceren wordt
      // center = absX + 11 = dot-centrum.
      const concept = ve?.ref ? ws.store.getConcept(ve.ref) : null;
      const isJunction =
        concept?.typeId === "archimate.AndJunction" || concept?.typeId === "archimate.OrJunction";
      const domSize = readNodeSize(n.id) ?? { width: 140, height: 60, banenKaart: false };
      const size = isJunction && !domSize.banenKaart ? { width: 22, height: 22 } : domSize;
      const parentId = ve?.containerId;
      const parentAbs = parentId ? absOf(parentId) : null;
      items.push({
        id: n.id,
        absX: abs.x,
        absY: abs.y,
        width: size.width,
        height: size.height,
        parentAbs,
      });
    }
    return items;
  };

  // ── Same size ────────────────────────────────────────────────────────────

  /**
   * Gelijke maat, met het **eerst geselecteerde object** als maatstaf.
   *
   * Tot 2026-09-01 nam 'ie de grootste van de selectie. Dat is willekeurig:
   * je kunt het niet sturen, en je ziet pas achteraf welk vakje gewonnen
   * heeft. Elk tekenprogramma gebruikt het eerst aangeklikte object als anker,
   * en dat is ook uit te leggen: je wijst aan wat de maat is en daarna wie 'm
   * moet volgen.
   *
   * Het anker komt uit `ui.selectionAnchorConceptId`, die Canvas zet bij het
   * aanklikken. Is 'ie leeg — na een sleepselectie bestaat er geen
   * klikvolgorde — dan valt 'ie terug op de grootste, het oude gedrag.
   */
  const equalSize = (axis: "width" | "height" | "both") => {
    if (!canAlign) return;
    const items = collectItems();
    if (items.length < 2) return;
    const ws = useWorkspaceData.getState();
    const anker = items.find((i) => i.id === ankerNodeId);
    const refWidth = anker?.width ?? Math.max(...items.map((i) => i.width));
    const refHeight = anker?.height ?? Math.max(...items.map((i) => i.height));
    for (const item of items) {
      if (anker && item.id === anker.id) continue;
      const newWidth = axis === "width" || axis === "both" ? refWidth : item.width;
      const newHeight = axis === "height" || axis === "both" ? refHeight : item.height;
      if (newWidth === item.width && newHeight === item.height) continue;
      ws.resizeNodeOnCanvas(workspaceId!, branchId!, item.id, {
        width: newWidth,
        height: newHeight,
      });
    }
  };

  // ── Edge orthogonalization ───────────────────────────────────────────────

  // The button is enabled whenever a single edge is selected — both
  // the "≥ 1 waypoint" path AND the "no waypoints" path can produce a
  // straighter line. The actual feasibility check (boxes overlap on the
  // relevant axis) lives inside `orthogonalize` for the no-waypoint
  // case; if it fails the user gets a `lastError` toast.
  const canOrthogonalize = (() => {
    if (!selectedRelationId || !workspaceId || !branchId) return false;
    const ws = useWorkspaceData.getState();
    const diagramId = ws.activeDiagramId;
    if (!diagramId) return false;
    return (
      vsGet(ws.store.getDiagram(diagramId)?.viewState ?? {}, selectedRelationId) !== undefined ||
      ws.store.listRelations().some((r) => r.id === selectedRelationId)
    );
  })();

  // Cmd/Ctrl+Shift+L-shortcut bridge: Editor.tsx dispatcht
  // `ae:straighten-relation` wanneer er een relatie geselecteerd is.
  // De useEffect-deps zorgen dat de listener altijd de actuele
  // selectedRelationId capture't.
  useEffect(() => {
    const onStraighten = () => {
      if (!useSession.getState().readOnly) orthogonalize();
    };
    window.addEventListener("ae:straighten-relation", onStraighten);
    return () => window.removeEventListener("ae:straighten-relation", onStraighten);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRelationId, workspaceId, branchId]);

  const orthogonalize = () => {
    if (!selectedRelationId || !workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    const diagramId = ws.activeDiagramId;
    if (!diagramId) return;
    const diagram = ws.store.getDiagram(diagramId);
    if (!diagram) return;
    const ve = vsGet(diagram.viewState, selectedRelationId, "orthogonalize");

    // Find the relation so we know which nodes are source and target.
    const relation = ws.store.listRelations().find((r) => r.id === selectedRelationId);
    if (!relation) return;

    // Build first-instance map (canonical node id for each concept on the diagram).
    const firstInstance = new Map<string, string>();
    for (const [key, entry] of Object.entries(diagram.viewState)) {
      if (!entry.position || entry.hidden) continue;
      const cId = entry.ref ?? key;
      if (!firstInstance.has(cId) || key === cId) firstInstance.set(cId, key);
    }
    const sourceNodeId =
      ve?.sourceInstanceId ??
      firstInstance.get(relation.sourceConceptId) ??
      relation.sourceConceptId;
    const targetNodeId =
      ve?.targetInstanceId ??
      firstInstance.get(relation.targetConceptId) ??
      relation.targetConceptId;

    const sourceRect = nodeAbsoluteRect(diagram.viewState, sourceNodeId);
    const targetRect = nodeAbsoluteRect(diagram.viewState, targetNodeId);
    if (!sourceRect || !targetRect) return;

    // ── Gerald's spec (2026-05-27): xm = midden van HUIDIGE endpoint-
    // posities, NIET van box-centers. De lijn raakt het kader van een
    // object op een bepaald X-punt (= bepaald door de huidige
    // sourceAngle/targetAngle). xm is het gemiddelde van die twee
    // X-coordinaten. Daarna landt de gerechte lijn op (xm, y) op
    // beide kaders, met clamping aan de overlap-range.
    // ─────────────────────────────────────────────────────────────────
    const sourceCenter = {
      x: sourceRect.x + sourceRect.w / 2,
      y: sourceRect.y + sourceRect.h / 2,
    };
    const targetCenter = {
      x: targetRect.x + targetRect.w / 2,
      y: targetRect.y + targetRect.h / 2,
    };

    // Huidig source endpoint (source-leidend). RoutedEdge's render-
    // fallback wanneer sourceAngle undefined is: richting eerste
    // waypoint (als die er is), anders richting target-center. Target's
    // endpoint snapt straks naar dezelfde xm/ym, dus die hoeven we
    // hier niet uit te rekenen.
    const wpListNow = ve?.waypoints;
    const firstWpNow =
      wpListNow && wpListNow.length > 0 ? (wpListNow[0] as { x: number; y: number }) : undefined;
    const effSourceAngle =
      ve?.sourceAngle ?? angleFromPoint(sourceCenter, firstWpNow ?? targetCenter);
    const p0 = pointFromAngle(
      { x: sourceRect.x, y: sourceRect.y, w: sourceRect.w, h: sourceRect.h },
      effSourceAngle,
    );

    // ── Lijn MET waypoints: snap intermediates orthogonaal ──────────
    // Per Gerald-spec: bij een lijn met punten worden alle secties
    // recht gemaakt en blijven de begin/eind (source/target) leidend.
    // Source/target angles + endpoints blijven onveranderd; alleen
    // de intermediate waypoints schuiven naar axis-aligned posities.
    if (wpListNow && wpListNow.length > 0) {
      const lastWpForTargetAngle = wpListNow[wpListNow.length - 1] as Pt;
      const effTargetAngleForPn =
        ve?.targetAngle ?? angleFromPoint(targetCenter, lastWpForTargetAngle);
      const pn = pointFromAngle(
        { x: targetRect.x, y: targetRect.y, w: targetRect.w, h: targetRect.h },
        effTargetAngleForPn,
      );
      const snapped = orthogonalizeWaypoints(p0, wpListNow as Pt[], pn);
      ws.setRelationGeometryOnCanvas(
        workspaceId,
        branchId,
        selectedRelationId,
        snapped,
        // Angles ongewijzigd doorgeven (= undefined → leave alone in
        // setRelationGeometryOnCanvas). Source/target endpoints
        // blijven dus precies waar ze nu staan.
        undefined,
        undefined,
      );
      return;
    }

    // ── Lijn ZONDER waypoints: source-leidende simpele formule ──────
    // Oriëntatie: gebruik box-centers (stabiele referentie) om te
    // beslissen of de lijn vooral verticaal of horizontaal loopt.
    // Endpoints kunnen door scheve angles diagonaal staan zonder dat
    // het de bedoeling is.
    const dCx = targetCenter.x - sourceCenter.x;
    const dCy = targetCenter.y - sourceCenter.y;

    // Overlap-check op beide assen vooraf, zodat we de keuze van
    // axis-classificatie kunnen overrulen wanneer de eerste keuze
    // geen overlap heeft (boxes kunnen wel op X overlappen maar niet
    // op Y of vice versa — dan dwingen we naar de werkende as).
    const xLoPre = Math.max(sourceRect.x, targetRect.x);
    const xHiPre = Math.min(sourceRect.x + sourceRect.w, targetRect.x + targetRect.w);
    const yLoPre = Math.max(sourceRect.y, targetRect.y);
    const yHiPre = Math.min(sourceRect.y + sourceRect.h, targetRect.y + targetRect.h);
    const xOverlap = xHiPre >= xLoPre;
    const yOverlap = yHiPre >= yLoPre;
    let vertical = Math.abs(dCy) >= Math.abs(dCx);
    // Edge-case: de boxes overlappen niet op de as die we volgens
    // de dCx/dCy-classificatie zouden willen gebruiken. Als de andere
    // as wél overlap heeft, schakel daarheen (de lijn kan dan
    // alsnog recht gemaakt worden, alleen in een andere oriëntatie
    // dan de "natuurlijke" volgens de box-positie).
    if (vertical && !xOverlap && yOverlap) vertical = false;
    if (!vertical && !yOverlap && xOverlap) vertical = true;

    // Center-pinning: bij endpoints op een AndJunction/OrJunction OF
    // op een héél klein concept (≤30×30 — typisch hub-dots of custom
    // junction-stand-ins) MOET het exit-punt op de center liggen.
    // Anders landt 'm op de rand, wat bij een 22×22 cirkel of 9×9 box
    // visueel ten koste gaat van een schuine lijn.
    // Resolveert de width/height via viewState OR concept-rect-default;
    // wanneer beide ontbreken vallen we terug op niet-pinnen.
    const isCenterPinned = (instanceId: string): boolean => {
      const veCheck = diagram.viewState[instanceId];
      if (!veCheck) return false;
      const refId = veCheck.ref ?? instanceId;
      const concept = ws.store.getConcept(refId);
      if (
        concept?.typeId === "archimate.AndJunction" ||
        concept?.typeId === "archimate.OrJunction"
      ) {
        return true;
      }
      // Klein-concept-pinning: max(w,h) ≤ 30 = visueel te klein om
      // off-center-exits cleanly te ondersteunen.
      const w = veCheck.width;
      const h = veCheck.height;
      if (w !== undefined && h !== undefined && w <= 30 && h <= 30) return true;
      return false;
    };
    const sourceIsCircle = isCenterPinned(sourceNodeId);
    const targetIsCircle = isCenterPinned(targetNodeId);

    if (vertical) {
      const xLo = xLoPre;
      const xHi = xHiPre;
      if (xHi < xLo) {
        useWorkspaceData.setState({
          lastError: t("ribbon.straightenNoHorizontalOverlap"),
        });
        return;
      }
      // xm = X van het HUIDIGE source-endpoint (source-leidend, per
      // Gerald-spec 2026-05-27). Target endpoint snapt naar dezelfde
      // X om de lijn recht te krijgen. Box-centers spelen geen rol —
      // tenzij een van de endpoints een cirkel is, dan moet xm
      // overeenkomen met diens center (cirkel kan visueel alleen
      // recht-omhoog/-onder uitgaan vanuit center).
      let avgX: number;
      if (sourceIsCircle && targetIsCircle) avgX = (sourceCenter.x + targetCenter.x) / 2;
      else if (sourceIsCircle) avgX = sourceCenter.x;
      else if (targetIsCircle) avgX = targetCenter.x;
      else avgX = p0.x;
      const x = Math.min(Math.max(avgX, xLo), xHi);
      // Source exit op de SAME edge richting de target (boven of onder
      // afhankelijk van de tekenrichting). Idem voor target.
      const sourceEdgeY = dCy >= 0 ? sourceRect.y + sourceRect.h : sourceRect.y;
      const targetEdgeY = dCy >= 0 ? targetRect.y : targetRect.y + targetRect.h;
      const newSourceAngle = angleFromPoint(sourceCenter, { x, y: sourceEdgeY });
      const newTargetAngle = angleFromPoint(targetCenter, { x, y: targetEdgeY });
      ws.setRelationGeometryOnCanvas(
        workspaceId,
        branchId,
        selectedRelationId,
        null, // waypoints clear — Gerald's spec: rechte lijn zonder bochten
        newSourceAngle,
        newTargetAngle,
      );
      return;
    }

    // Horizontale lijn: spiegel van vertical-pad over de x/y-assen.
    const yLo = yLoPre;
    const yHi = yHiPre;
    if (yHi < yLo) {
      useWorkspaceData.setState({
        lastError: t("ribbon.straightenNoVerticalOverlap"),
      });
      return;
    }
    // ym = Y van het HUIDIGE source-endpoint (source-leidend, zie
    // comment bij vertical-tak). Target endpoint snapt naar dezelfde Y.
    let avgY: number;
    if (sourceIsCircle && targetIsCircle) avgY = (sourceCenter.y + targetCenter.y) / 2;
    else if (sourceIsCircle) avgY = sourceCenter.y;
    else if (targetIsCircle) avgY = targetCenter.y;
    else avgY = p0.y;
    const y = Math.min(Math.max(avgY, yLo), yHi);
    const sourceEdgeX = dCx >= 0 ? sourceRect.x + sourceRect.w : sourceRect.x;
    const targetEdgeX = dCx >= 0 ? targetRect.x : targetRect.x + targetRect.w;
    const newSourceAngle = angleFromPoint(sourceCenter, { x: sourceEdgeX, y });
    const newTargetAngle = angleFromPoint(targetCenter, { x: targetEdgeX, y });
    ws.setRelationGeometryOnCanvas(
      workspaceId,
      branchId,
      selectedRelationId,
      null, // waypoints clear
      newSourceAngle,
      newTargetAngle,
    );
  };

  // ── Z-stack manipulatie (Volgorde-ribbon-groep) ─────────────────────────
  // Aangeroepen door de 4 toolbar-knoppen "Naar voren / Naar achteren /
  // Helemaal bovenop / Helemaal onderop". Werkt op alle geselecteerde
  // concept-instances tegelijk. Eén undo-entry per call.
  const changeZ = (action: "forward" | "backward" | "toFront" | "toBack") => {
    if (!hasSelection || !workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    ws.changeZOrderOnCanvas(
      workspaceId,
      branchId,
      selected.map((n) => n.id),
      action,
    );
  };

  const align = (action: AlignAction) => {
    if (!canAlign) return;
    const items = collectItems();
    if (items.length < 2) return;
    const ws = useWorkspaceData.getState();

    // Uitlijnen op het **eerst aangeklikte** object: dat blijft staan waar
    // 't staat en de rest schuift ernaartoe. Zonder anker — na een
    // sleepselectie bestaat er geen klikvolgorde — geldt de omhullende
    // rechthoek van de selectie, het oude gedrag.
    const anker = items.find((i) => i.id === ankerNodeId);
    const minLeft = anker?.absX ?? Math.min(...items.map((i) => i.absX));
    const maxRight = anker
      ? anker.absX + anker.width
      : Math.max(...items.map((i) => i.absX + i.width));
    const minTop = anker?.absY ?? Math.min(...items.map((i) => i.absY));
    const maxBottom = anker
      ? anker.absY + anker.height
      : Math.max(...items.map((i) => i.absY + i.height));
    const centerX = anker ? anker.absX + anker.width / 2 : (minLeft + maxRight) / 2;
    const centerY = anker ? anker.absY + anker.height / 2 : (minTop + maxBottom) / 2;

    for (const item of items) {
      let newAbsX = item.absX;
      let newAbsY = item.absY;
      switch (action) {
        case "left":
          newAbsX = minLeft;
          break;
        case "right":
          newAbsX = maxRight - item.width;
          break;
        case "top":
          newAbsY = minTop;
          break;
        case "bottom":
          newAbsY = maxBottom - item.height;
          break;
        case "center-x":
          newAbsX = centerX - item.width / 2;
          break;
        case "center-y":
          newAbsY = centerY - item.height / 2;
          break;
      }
      const newLocal = item.parentAbs
        ? { x: newAbsX - item.parentAbs.x, y: newAbsY - item.parentAbs.y }
        : { x: newAbsX, y: newAbsY };
      const ve = ws.store.getDiagram(ws.activeDiagramId ?? "")?.viewState[item.id];
      if (ve?.position?.x === newLocal.x && ve?.position?.y === newLocal.y) continue;
      ws.moveConceptOnCanvas(workspaceId!, branchId!, item.id, newLocal);
    }

    // ── Edge-alignment voor center-x en center-y ─────────────────────
    // Gerald-spec 2026-05-27: bij verticaal/horizontaal centreren
    // worden ook geselecteerde lijnen rechtgetrokken op centerY/centerX.
    // Lijn wordt een rechte 2-puntsverbinding (waypoints cleared), met
    // source/target endpoints op de boxes' randen zo dichtbij mogelijk
    // bij de center-coord, en de zijde van de box facing-toward-other-
    // endpoint. Andere alignment-acties (left/right/top/bottom) raken
    // edges niet aan — voor die acties is een edge-equivalent meerduidig.
    if (action !== "center-x" && action !== "center-y") return;
    if (selectedRelationIds.length === 0) return;
    const diagram = ws.store.getDiagram(ws.activeDiagramId ?? "");
    if (!diagram) return;
    // Per VOORKOMEN, niet per relatie: het rechttrekken schrijft naar de
    // viewState van dít voorkomen, en op een diagram waar dezelfde reeks
    // meerdere keren staat zou werken op de relatie-id een lijn in een
    // andere rij verzetten.
    for (const entryKey of selectedEdgeKeys) {
      const ve = diagram.viewState[entryKey] ?? vsGet(diagram.viewState, entryKey, "toolbar-bulk");
      const relId = ve?.ref ?? entryKey;
      const relation = ws.store.listRelations().find((r) => r.id === relId);
      if (!relation) continue;
      const firstInstance = new Map<string, string>();
      for (const [key, entry] of Object.entries(diagram.viewState)) {
        if (!entry.position || entry.hidden) continue;
        const cId = entry.ref ?? key;
        if (!firstInstance.has(cId) || key === cId) firstInstance.set(cId, key);
      }
      const sourceNodeId =
        ve?.sourceInstanceId ??
        firstInstance.get(relation.sourceConceptId) ??
        relation.sourceConceptId;
      const targetNodeId =
        ve?.targetInstanceId ??
        firstInstance.get(relation.targetConceptId) ??
        relation.targetConceptId;
      const sRect = nodeAbsoluteRect(diagram.viewState, sourceNodeId);
      const tRect = nodeAbsoluteRect(diagram.viewState, targetNodeId);
      if (!sRect || !tRect) continue;
      const sCenter = { x: sRect.x + sRect.w / 2, y: sRect.y + sRect.h / 2 };
      const tCenter = { x: tRect.x + tRect.w / 2, y: tRect.y + tRect.h / 2 };

      if (action === "center-y") {
        // Horizontale lijn op Y=centerY. Bepaal welke kant van source/
        // target de andere endpoint zit en exit op die kant.
        const sourceFacesRight = tCenter.x >= sCenter.x;
        const sExitX = sourceFacesRight ? sRect.x + sRect.w : sRect.x;
        const tExitX = sourceFacesRight ? tRect.x : tRect.x + tRect.w;
        // Clamp Y aan box-Y-range zodat 't endpoint op de box-rand zit
        // (anders projecteert pointFromAngle naar de hoek).
        const sY = Math.min(Math.max(centerY, sRect.y), sRect.y + sRect.h);
        const tY = Math.min(Math.max(centerY, tRect.y), tRect.y + tRect.h);
        const newSourceAngle = angleFromPoint(sCenter, { x: sExitX, y: sY });
        const newTargetAngle = angleFromPoint(tCenter, { x: tExitX, y: tY });
        ws.setRelationGeometryOnCanvas(
          workspaceId!,
          branchId!,
          entryKey,
          null,
          newSourceAngle,
          newTargetAngle,
        );
      } else {
        // center-x: verticale lijn op X=centerX
        const sourceFacesDown = tCenter.y >= sCenter.y;
        const sExitY = sourceFacesDown ? sRect.y + sRect.h : sRect.y;
        const tExitY = sourceFacesDown ? tRect.y : tRect.y + tRect.h;
        const sX = Math.min(Math.max(centerX, sRect.x), sRect.x + sRect.w);
        const tX = Math.min(Math.max(centerX, tRect.x), tRect.x + tRect.w);
        const newSourceAngle = angleFromPoint(sCenter, { x: sX, y: sExitY });
        const newTargetAngle = angleFromPoint(tCenter, { x: tX, y: tExitY });
        ws.setRelationGeometryOnCanvas(
          workspaceId!,
          branchId!,
          entryKey,
          null,
          newSourceAngle,
          newTargetAngle,
        );
      }
    }
  };

  // For the swatch indicators we sample the FIRST selected edge's
  // value. Bulk-write applies to all selected. If the user wants to
  // preserve heterogeneous colours, they shouldn't pick a new one —
  // multi-select is opt-in via Cmd/Ctrl/Alt-click anyway.
  const lineColorValue = (() => {
    if (!firstSelectedEdgeId || !workspaceId || !branchId) return undefined;
    const ws = useWorkspaceData.getState();
    const dId = ws.activeDiagramId;
    if (!dId) return undefined;
    return vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, firstSelectedEdgeId)?.lineColor;
  })();
  const labelBgColorValue = (() => {
    if (!firstSelectedEdgeId || !workspaceId || !branchId) return undefined;
    const ws = useWorkspaceData.getState();
    const dId = ws.activeDiagramId;
    if (!dId) return undefined;
    return vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, firstSelectedEdgeId)?.labelBgColor;
  })();
  const labelTextColorValue = (() => {
    if (!firstSelectedEdgeId || !workspaceId || !branchId) return undefined;
    const ws = useWorkspaceData.getState();
    const dId = ws.activeDiagramId;
    if (!dId) return undefined;
    return vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, firstSelectedEdgeId)?.labelTextColor;
  })();
  // Routing dropdown value: pick first edge's routing. When mixed
  // across the selected set we'd ideally show a "mixed" placeholder;
  // for v1 we just show the first's value since the user is about to
  // overwrite anyway.
  const routingValue = (() => {
    if (!firstSelectedEdgeId || !workspaceId || !branchId) return "straight" as const;
    const ws = useWorkspaceData.getState();
    const dId = ws.activeDiagramId;
    if (!dId) return "straight" as const;
    return (
      vsGet(ws.store.getDiagram(dId)?.viewState ?? {}, firstSelectedEdgeId)?.routing ?? "straight"
    );
  })();

  // Line-style dropdown waarde — los van routing. "default" = geen
  // viewState-override gezet, dus de relatie-type's / aspect's eigen
  // pattern wint. We sampelen eerst de eerste edge; als er geen edge
  // maar wel een node geselecteerd is, sampelen we die node's
  // `nodeStyle.borderStyle` zodat de dropdown ook bij een geselecteerde
  // rechthoek z'n huidige stijl toont.
  const lineStyleValue = (() => {
    if (!workspaceId || !branchId) return "default" as const;
    const ws = useWorkspaceData.getState();
    const dId = ws.activeDiagramId;
    if (!dId) return "default" as const;
    const diagram = ws.store.getDiagram(dId);
    if (!diagram) return "default" as const;
    if (firstSelectedEdgeId) {
      return vsGet(diagram.viewState, firstSelectedEdgeId)?.lineStyle ?? "default";
    }
    const firstNode = selected[0];
    if (firstNode) {
      return diagram.viewState[firstNode.id]?.nodeStyle?.borderStyle ?? "default";
    }
    return "default" as const;
  })();

  const resetFormatting = () => {
    if (!workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    const clear: NodeStylePatch = {
      bold: undefined,
      italic: undefined,
      underline: undefined,
      fontSize: undefined,
      fontFamily: undefined,
      fontColor: undefined,
      fillColor: undefined,
      strokeColor: undefined,
    };
    for (const n of selected) {
      ws.setNodeStyleOnCanvas(workspaceId, branchId, n.id, clear as Partial<NodeStyle>);
    }
  };

  return (
    // Een vaste balk bovenaan de werkruimte (`RibbonSlot`), die zich aanpast
    // aan de breedte: past niet alles, dan klappen de minst gebruikte groepen
    // in tot een knop met uitklapmenu (`RibbonLayout.tsx`).
    <RibbonBar
      data-scope="toolbar"
      className="ledger w-full overflow-x-auto overflow-y-hidden border-(--ld-border) border-b bg-(--ld-surface) text-(--ld-text)"
      innerClassName="items-stretch divide-x divide-(--ld-border) border-(--ld-border) border-x"
      role="toolbar"
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* ── Lettertype ──────────────────────────────────────────────────── */}
      <RibbonGroup
        id="font"
        hidden={readOnly}
        label={t("ribbon.groups.font")}
        shortLabel={t("ribbon.groupsShort.font")}
        priority={8}
      >
        <Row>
          <ToolbarButton
            disabled={!hasSelection}
            onClick={() => toggle("bold")}
            title={t("ribbon.bold")}
            label="B"
            active={!!firstStyle.bold}
            className="font-bold"
          />
          <ToolbarButton
            disabled={!hasSelection}
            onClick={() => toggle("italic")}
            title={t("ribbon.italic")}
            label="I"
            active={!!firstStyle.italic}
            className="italic"
          />
          <ToolbarButton
            disabled={!hasSelection}
            onClick={() => toggle("underline")}
            title={t("ribbon.underline")}
            label="U"
            active={!!firstStyle.underline}
            className="underline"
          />
          <select
            disabled={!hasSelection}
            className="h-7 w-[110px] rounded border border-(--ld-border) bg-(--ld-surface) px-1 text-(--ld-text) text-xs disabled:border-(--ld-control) disabled:bg-(--ld-raised) disabled:text-(--ld-muted) disabled:opacity-60 focus:outline-none"
            title={t("ribbon.fontFamily")}
            value={firstStyle.fontFamily ?? ""}
            onChange={(e) => applyStyle({ fontFamily: e.target.value || undefined })}
          >
            {FONT_FAMILIES.map((f) => (
              <option key={f.value} value={f.value}>
                {f.value === "" ? t("ribbon.default") : f.label}
              </option>
            ))}
          </select>
        </Row>
        <Row>
          <select
            disabled={!hasSelection}
            className="h-7 w-[60px] rounded border border-(--ld-border) bg-(--ld-surface) px-1 text-(--ld-text) text-xs disabled:border-(--ld-control) disabled:bg-(--ld-raised) disabled:text-(--ld-muted) disabled:opacity-60 focus:outline-none"
            title={t("ribbon.fontSize")}
            value={firstStyle.fontSize ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              applyStyle({ fontSize: v ? Number(v) : undefined });
            }}
          >
            <option value="">px</option>
            {FONT_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {/* Tekst-uitlijning binnen het label (concept én edge). Werkt
              op alle geselecteerde items tegelijk; voor nodes via
              NodeStyle.labelTextAlign, voor edges via
              ViewElementState.labelTextAlign. */}
          {(["left", "center", "right"] as const).map((align) => {
            const isActive = hasSelection && (firstStyle.labelTextAlign ?? "center") === align;
            const isActiveEdge =
              hasEdgeSelection &&
              !hasSelection &&
              (() => {
                const ws = useWorkspaceData.getState();
                const diag = ws.activeDiagramId ? ws.store.getDiagram(ws.activeDiagramId) : null;
                const id = selectedRelationIds[0];
                const ve = diag && id ? diag.viewState[id] : undefined;
                return (ve?.labelTextAlign ?? "center") === align;
              })();
            const symbol = <TextAlignIcon align={align} />;
            const labelText =
              align === "left"
                ? t("ribbon.alignLeft")
                : align === "right"
                  ? t("ribbon.alignRight")
                  : t("ribbon.alignCenter");
            return (
              <ToolbarButton
                key={align}
                disabled={!hasSelection && !hasEdgeSelection}
                onClick={() => {
                  // Cleared-to-default: klik op center wist het veld
                  // (geen unnecessary persistence van de default-waarde).
                  const next = align === "center" ? null : align;
                  if (hasSelection) {
                    applyStyle({
                      labelTextAlign: next === null ? undefined : next,
                    });
                  }
                  if (hasEdgeSelection && workspaceId && branchId) {
                    const ws = useWorkspaceData.getState();
                    for (const id of selectedEdgeKeys) {
                      ws.setRelationLabelTextAlignOnCanvas(workspaceId, branchId, id, next);
                    }
                  }
                }}
                title={labelText}
                label={symbol}
                active={isActive || isActiveEdge}
              />
            );
          })}
          {/* Word-wrap-toggles: hyphenate vs break-anywhere. Mutex —
              klikken op de actieve toggle wist 't (= geen wrap). */}
          {(["hyphenate", "break-anywhere"] as const).map((mode) => {
            const isActive = hasSelection && firstStyle.labelWrap === mode;
            const isActiveEdge =
              hasEdgeSelection &&
              !hasSelection &&
              (() => {
                const ws = useWorkspaceData.getState();
                const diag = ws.activeDiagramId ? ws.store.getDiagram(ws.activeDiagramId) : null;
                const id = selectedRelationIds[0];
                const ve = diag && id ? diag.viewState[id] : undefined;
                return ve?.labelWrap === mode;
              })();
            const symbol = mode === "hyphenate" ? "Hy-" : "↵";
            const labelText =
              mode === "hyphenate"
                ? t("ribbon.labelWrapHyphenate")
                : t("ribbon.labelWrapBreakAnywhere");
            return (
              <ToolbarButton
                key={mode}
                disabled={!hasSelection && !hasEdgeSelection}
                onClick={() => {
                  const currentActive = isActive || isActiveEdge;
                  const next = currentActive ? null : mode;
                  if (hasSelection) {
                    applyStyle({
                      labelWrap: next === null ? undefined : next,
                    });
                  }
                  if (hasEdgeSelection && workspaceId && branchId) {
                    const ws = useWorkspaceData.getState();
                    for (const id of selectedEdgeKeys) {
                      ws.setRelationLabelWrapOnCanvas(workspaceId, branchId, id, next);
                    }
                  }
                }}
                title={labelText}
                label={symbol}
                active={isActive || isActiveEdge}
              />
            );
          })}
          {/* Tekst een kwartslag draaien. Bedoeld voor een smal, hoog vakje
              en voor de baannamen op het banen-diagram, die Sharp verticaal
              zet (Fig 8.1, p204). Klikken op de actieve stand zet 'm terug. */}
          <ToolbarButton
            disabled={!hasSelection}
            onClick={() =>
              applyStyle({
                labelOrientation:
                  firstStyle.labelOrientation === "vertical" ? undefined : "vertical",
              })
            }
            title={t("ribbon.labelVertical")}
            label={<span style={{ writingMode: "vertical-rl" }}>A</span>}
            active={hasSelection && firstStyle.labelOrientation === "vertical"}
          />
          <ToolbarButton
            disabled={!hasSelection}
            onClick={resetFormatting}
            title={t("ribbon.clearFormatting")}
            label="✕"
          />
        </Row>
      </RibbonGroup>

      {/* ── Kleur ───────────────────────────────────────────────────────── */}
      <RibbonGroup
        id="colour"
        hidden={readOnly}
        label={t("ribbon.groups.colour")}
        shortLabel={t("ribbon.groupsShort.colour")}
        priority={7}
      >
        <Row>
          <SwatchColorPicker
            title={t("ribbon.textColour")}
            value={firstStyle.fontColor}
            disabled={!hasSelection}
            allowClear
            onChange={(c) => applyStyle({ fontColor: c })}
            renderTrigger={({ color, onClick, open }) => (
              <button
                type="button"
                title={t("ribbon.textColour")}
                disabled={!hasSelection}
                onClick={onClick}
                className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-(--ld-text) text-xs hover:bg-(--ld-active) ${open ? "bg-(--ld-active)" : ""} ${!hasSelection ? "pointer-events-none opacity-30" : ""}`}
              >
                <span className="select-none">A</span>
                <span
                  className="absolute bottom-1 left-1 right-1 h-0.5 rounded"
                  style={{ backgroundColor: color ?? "#334155" }}
                />
              </button>
            )}
          />
          <SwatchColorPicker
            title={t("ribbon.fillColour")}
            value={firstStyle.fillColor}
            disabled={!hasSelection}
            allowClear
            allowTransparent
            onChange={(c) => applyStyle({ fillColor: c })}
            renderTrigger={({ color, onClick, open }) => (
              <button
                type="button"
                title={t("ribbon.fillColour")}
                disabled={!hasSelection}
                onClick={onClick}
                className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-xs hover:bg-(--ld-active) ${open ? "bg-(--ld-active)" : ""} ${!hasSelection ? "pointer-events-none opacity-30" : ""}`}
              >
                <span
                  className="h-4 w-4 rounded border border-(--ld-control)"
                  style={{ backgroundColor: color ?? "#ffffff" }}
                />
              </button>
            )}
          />
          {/*
            Per-node stroke (border) colour picker. Mirrors the fill-
            colour control next to it. Trigger is a hollow square with
            a coloured border so the visual cue matches what the node
            actually shows on canvas. Default = layer-derived border.
          */}
          <SwatchColorPicker
            title={t("ribbon.strokeColourPicker")}
            value={firstStyle.strokeColor}
            disabled={!hasSelection}
            allowClear
            // Transparant zetten van de stroke maakt een drawing.Rectangle
            // omkaderingsloos — fungeert dan als vrij tekstblok via de
            // bestaande naam. Zelfde optie ook in het AttributePanel.
            allowTransparent
            onChange={(c) => applyStyle({ strokeColor: c })}
            renderTrigger={({ color, onClick, open }) => (
              <button
                type="button"
                title={t("ribbon.strokeColourTitle")}
                disabled={!hasSelection}
                onClick={onClick}
                className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-xs hover:bg-(--ld-active) ${open ? "bg-(--ld-active)" : ""} ${!hasSelection ? "pointer-events-none opacity-30" : ""}`}
              >
                <span
                  className="h-4 w-4 rounded border-2 bg-(--ld-surface)"
                  style={{ borderColor: color ?? "#475569" }}
                />
              </button>
            )}
          />
        </Row>
        <Row>
          <SwatchColorPicker
            title={
              multiEdgeSelection
                ? t("ribbon.lineColourMulti", { n: selectedRelationIds.length })
                : t("ribbon.lineColour")
            }
            value={lineColorValue}
            disabled={!hasEdgeSelection}
            allowClear
            onChange={(c) => {
              if (!hasEdgeSelection || !workspaceId || !branchId) return;
              const ws = useWorkspaceData.getState();
              for (const id of selectedEdgeKeys) {
                ws.setRelationLineColorOnCanvas(workspaceId, branchId, id, c ?? null);
              }
            }}
            renderTrigger={({ color, onClick, open }) => (
              <button
                type="button"
                title={t("ribbon.lineColour")}
                disabled={!hasEdgeSelection}
                onClick={onClick}
                className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-xs hover:bg-(--ld-active) ${open ? "bg-(--ld-active)" : ""} ${!hasEdgeSelection ? "pointer-events-none opacity-30" : ""}`}
              >
                <span
                  className="block h-0.5 w-4 rounded"
                  style={{ backgroundColor: color ?? "#475569" }}
                />
                {multiEdgeSelection && (
                  <span className="-bottom-0.5 -right-0.5 absolute rounded bg-(--ld-primary) px-1 font-bold text-[0.625rem] text-(--ld-primary-ink) leading-none">
                    {selectedRelationIds.length}
                  </span>
                )}
              </button>
            )}
          />
          {/*
            Label-background colour for the selected edge. Default
            (no value) = transparent; user picks a colour to give the
            label an opaque box (handy when the line passes through a
            tinted area and the text-shadow halo isn't enough).
          */}
          <SwatchColorPicker
            title={
              multiEdgeSelection
                ? t("ribbon.labelBg.multi", { n: selectedRelationIds.length })
                : labelBgColorValue
                  ? t("ribbon.labelBg.set")
                  : t("ribbon.labelBg.unset")
            }
            value={labelBgColorValue}
            disabled={!hasEdgeSelection}
            allowClear
            clearLabel={t("ribbon.swatch.transparent")}
            onChange={(c) => {
              if (!hasEdgeSelection || !workspaceId || !branchId) return;
              const ws = useWorkspaceData.getState();
              for (const id of selectedEdgeKeys) {
                ws.setRelationLabelBgColorOnCanvas(workspaceId, branchId, id, c ?? null);
              }
            }}
            renderTrigger={({ color, onClick, open }) => (
              <button
                type="button"
                title={t("ribbon.labelBg.title")}
                disabled={!hasEdgeSelection}
                onClick={onClick}
                className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-xs font-semibold leading-none hover:bg-(--ld-active) ${open ? "bg-(--ld-active)" : ""} ${!hasEdgeSelection ? "pointer-events-none opacity-30" : ""}`}
              >
                <span
                  className="flex h-4 w-5 items-center justify-center rounded border border-(--ld-control) text-(--ld-text)"
                  style={{
                    backgroundColor: color ?? "transparent",
                    backgroundImage: color
                      ? undefined
                      : // Slate-300 vs the previous slate-200: gives a
                        // visible checker against the toolbar's beige/grey
                        // background. The lighter shade was getting lost
                        // and made the L look like it sat on plain white.
                        "linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%), linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%)",
                    backgroundSize: "6px 6px",
                    backgroundPosition: "0 0, 3px 3px",
                  }}
                >
                  L
                </span>
                {multiEdgeSelection && (
                  <span className="-bottom-0.5 -right-0.5 absolute rounded bg-(--ld-primary) px-1 font-bold text-[0.625rem] text-(--ld-primary-ink) leading-none">
                    {selectedRelationIds.length}
                  </span>
                )}
              </button>
            )}
          />
          {/*
            Label-text colour for the selected edge. Default (no value)
            = relation-type default (slate-600 voor gewone relaties,
            donker bij Influence). User picks een kleur om het label
            visueel te accentueren — handig op donkere achtergronden of
            voor warning-rode tekst boven kritieke relaties.
          */}
          <SwatchColorPicker
            title={
              multiEdgeSelection
                ? t("ribbon.labelText.multi", { n: selectedRelationIds.length })
                : labelTextColorValue
                  ? t("ribbon.labelText.set")
                  : t("ribbon.labelText.unset")
            }
            value={labelTextColorValue}
            disabled={!hasEdgeSelection}
            allowClear
            clearLabel={t("ribbon.default")}
            onChange={(c) => {
              if (!hasEdgeSelection || !workspaceId || !branchId) return;
              const ws = useWorkspaceData.getState();
              for (const id of selectedEdgeKeys) {
                ws.setRelationLabelTextColorOnCanvas(workspaceId, branchId, id, c ?? null);
              }
            }}
            renderTrigger={({ color, onClick, open }) => (
              <button
                type="button"
                title={t("ribbon.labelText.title")}
                disabled={!hasEdgeSelection}
                onClick={onClick}
                className={`relative flex h-7 w-7 cursor-pointer items-center justify-center rounded text-xs font-semibold leading-none hover:bg-(--ld-active) ${open ? "bg-(--ld-active)" : ""} ${!hasEdgeSelection ? "pointer-events-none opacity-30" : ""}`}
              >
                <span
                  className="flex h-4 w-5 items-center justify-center rounded border border-(--ld-control) bg-(--ld-surface)"
                  style={{
                    color: color ?? "#475569",
                  }}
                >
                  A
                </span>
                {multiEdgeSelection && (
                  <span className="-bottom-0.5 -right-0.5 absolute rounded bg-(--ld-primary) px-1 font-bold text-[0.625rem] text-(--ld-primary-ink) leading-none">
                    {selectedRelationIds.length}
                  </span>
                )}
              </button>
            )}
          />
        </Row>
      </RibbonGroup>

      {/* ── Uitlijnen ───────────────────────────────────────────────────── */}
      <RibbonGroup
        id="align"
        hidden={readOnly}
        label={t("ribbon.groups.align")}
        shortLabel={t("ribbon.groupsShort.align")}
        priority={5}
      >
        <Row>
          <ToolbarButton
            disabled={!canAlign}
            onClick={() => align("left")}
            title={t("ribbon.alignLeft")}
            label={<EdgeArrow deg={270} />}
          />
          <ToolbarButton
            disabled={!canAlign}
            onClick={() => align("center-x")}
            title={t("ribbon.alignCenterX")}
            label={<HCenterIcon />}
          />
          <ToolbarButton
            disabled={!canAlign}
            onClick={() => align("right")}
            title={t("ribbon.alignRight")}
            label={<EdgeArrow deg={90} />}
          />
        </Row>
        <Row>
          <ToolbarButton
            disabled={!canAlign}
            onClick={() => align("top")}
            title={t("ribbon.alignTop")}
            label={<EdgeArrow deg={0} />}
          />
          <ToolbarButton
            disabled={!canAlign}
            onClick={() => align("center-y")}
            title={t("ribbon.alignCenterY")}
            label={<VCenterIcon />}
          />
          <ToolbarButton
            disabled={!canAlign}
            onClick={() => align("bottom")}
            title={t("ribbon.alignBottom")}
            label={<EdgeArrow deg={180} />}
          />
        </Row>
      </RibbonGroup>

      {/* ── Volgorde (z-stack) ──────────────────────────────────────────── */}
      <RibbonGroup
        id="zStack"
        hidden={readOnly}
        label={t("ribbon.groups.zStack")}
        shortLabel={t("ribbon.groupsShort.zStack")}
        priority={4}
      >
        <Row>
          <ToolbarButton
            disabled={!magZOrder}
            onClick={() => changeZ("toFront")}
            title={t("ribbon.zStack.toFront")}
            label={<EdgeArrow deg={0} />}
          />
          <ToolbarButton
            disabled={!magZOrder}
            onClick={() => changeZ("forward")}
            title={t("ribbon.zStack.forward")}
            label={<PositionArrow deg={0} />}
          />
        </Row>
        <Row>
          <ToolbarButton
            disabled={!magZOrder}
            onClick={() => changeZ("toBack")}
            title={t("ribbon.zStack.toBack")}
            label={<EdgeArrow deg={180} />}
          />
          <ToolbarButton
            disabled={!magZOrder}
            onClick={() => changeZ("backward")}
            title={t("ribbon.zStack.backward")}
            label={<PositionArrow deg={180} />}
          />
        </Row>
      </RibbonGroup>

      {/* ── Tekst in object ─────────────────────────────────────────────── */}
      <RibbonGroup
        id="textInObject"
        hidden={readOnly}
        label={t("ribbon.groups.textInObject")}
        shortLabel={t("ribbon.groupsShort.textInObject")}
        priority={3}
      >
        {(() => {
          const ax = firstStyle.labelAlignX ?? "center";
          const ay = firstStyle.labelAlignY ?? "middle";
          const isCenterCenter = ax === "center" && ay === "middle";
          const cell = (
            x: "left" | "center" | "right",
            y: "top" | "middle" | "bottom",
            title: string,
          ) => (
            <ToolbarButton
              disabled={!hasSelection}
              onClick={() =>
                applyStyle({
                  labelAlignX: x,
                  labelAlignY: y,
                  // If clicking dead-centre, force outside off (it's not
                  // applicable). Otherwise keep whatever the user picked.
                  ...(x === "center" && y === "middle" ? { labelOutside: false } : {}),
                })
              }
              title={
                firstStyle.labelOutside && !isCenterCenter
                  ? `${title}${t("ribbon.label.outsideSuffix")}`
                  : title
              }
              label={<PositionArrow deg={POSITION_ARROW_DEG[`${x}-${y}`] ?? 0} />}
              active={ax === x && ay === y}
            />
          );
          return (
            <div className="flex items-center gap-1.5">
              <div
                className="grid shrink-0 gap-0.5"
                style={{ gridTemplateColumns: "repeat(3, 1.75rem)" }}
              >
                {cell("left", "top", t("ribbon.label.topLeft"))}
                {cell("center", "top", t("ribbon.label.top"))}
                {cell("right", "top", t("ribbon.label.topRight"))}
                {cell("left", "middle", t("ribbon.label.left"))}
                {/* Centre cell: bigger black dot, no extra chrome. */}
                <button
                  type="button"
                  disabled={!hasSelection}
                  onClick={() =>
                    applyStyle({
                      labelAlignX: "center",
                      labelAlignY: "middle",
                      labelOutside: false,
                    })
                  }
                  title={t("ribbon.label.center")}
                  aria-label={t("ribbon.label.center")}
                  aria-pressed={isCenterCenter}
                  className="flex h-7 w-7 items-center justify-center rounded text-(--ld-text) hover:bg-(--ld-active) disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden>
                    <circle cx="8" cy="8" r="4.5" />
                  </svg>
                </button>
                {cell("right", "middle", t("ribbon.label.right"))}
                {cell("left", "bottom", t("ribbon.label.bottomLeft"))}
                {cell("center", "bottom", t("ribbon.label.bottom"))}
                {cell("right", "bottom", t("ribbon.label.bottomRight"))}
              </div>
              <label
                className={`flex cursor-pointer items-center gap-1 rounded px-1 text-xs text-(--ld-text) hover:bg-(--ld-active) ${!hasSelection || isCenterCenter ? "pointer-events-none opacity-30" : ""}`}
                title={t("ribbon.label.outsideTitle")}
              >
                <input
                  type="checkbox"
                  className="h-3 w-3"
                  disabled={!hasSelection || isCenterCenter}
                  checked={!!firstStyle.labelOutside}
                  onChange={(e) => applyStyle({ labelOutside: e.target.checked })}
                />
                {t("ribbon.label.outside")}
              </label>
              {/* Verberg corner-icon — onafhankelijk van label-uitlijning. */}
              <label
                className={`flex cursor-pointer items-center gap-1 rounded px-1 text-xs text-(--ld-text) hover:bg-(--ld-active) ${!magIconen ? "pointer-events-none opacity-30" : ""}`}
                title={t("ribbon.label.hideIconTitle")}
              >
                <input
                  type="checkbox"
                  className="h-3 w-3"
                  disabled={!magIconen}
                  checked={!!firstStyle.hideIcon}
                  onChange={(e) => applyStyle({ hideIcon: e.target.checked })}
                />
                {t("ribbon.label.hideIcon")}
              </label>
            </div>
          );
        })()}
      </RibbonGroup>

      {/* ── Grootte ─────────────────────────────────────────────────────── */}
      <RibbonGroup
        id="size"
        hidden={readOnly}
        label={t("ribbon.groups.size")}
        shortLabel={t("ribbon.groupsShort.size")}
        priority={2}
      >
        <Row>
          <ToolbarButton
            disabled={!canAlign}
            onClick={() => equalSize("height")}
            title={t("ribbon.equalHeight")}
            label="↕"
          />
          <ToolbarButton
            disabled={!magMaat}
            onClick={() => {
              // Toggle ellips: aan = border-radius 50%, uit = undefined
              // (terug naar default-shape voor het type).
              const isEllipse = firstStyle.borderRadiusOverride === "50%";
              applyStyle({ borderRadiusOverride: isEllipse ? undefined : "50%" });
            }}
            title={t("ribbon.shapeEllipse")}
            label="⬭"
            active={hasSelection && firstStyle.borderRadiusOverride === "50%"}
          />
        </Row>
        <Row>
          <ToolbarButton
            disabled={!canAlign}
            onClick={() => equalSize("width")}
            title={t("ribbon.equalWidth")}
            label="↔"
          />
          <ToolbarButton
            disabled={!magMaat}
            onClick={() => {
              // Cirkel = vierkant + border-radius 50%. Per item resize
              // naar de grootste van eigen w/h.
              if (!workspaceId || !branchId) return;
              const ws = useWorkspaceData.getState();
              for (const item of collectItems()) {
                const side = Math.max(item.width, item.height);
                if (side !== item.width || side !== item.height) {
                  ws.resizeNodeOnCanvas(workspaceId, branchId, item.id, {
                    width: side,
                    height: side,
                  });
                }
              }
              applyStyle({ borderRadiusOverride: "50%" });
            }}
            title={t("ribbon.shapeCircle")}
            label="●"
          />
        </Row>
        <Row>
          <ToolbarButton
            disabled={!canAlign}
            onClick={() => equalSize("both")}
            title={t("ribbon.equalBoth")}
            label="⛶"
          />
          <ToolbarButton
            disabled={!magMaat}
            onClick={() => {
              // Vierkant = resize per item naar grootste w/h, geen
              // border-radius wijziging.
              if (!workspaceId || !branchId) return;
              const ws = useWorkspaceData.getState();
              for (const item of collectItems()) {
                const side = Math.max(item.width, item.height);
                if (side !== item.width || side !== item.height) {
                  ws.resizeNodeOnCanvas(workspaceId, branchId, item.id, {
                    width: side,
                    height: side,
                  });
                }
              }
            }}
            title={t("ribbon.shapeSquare")}
            label="■"
          />
        </Row>
      </RibbonGroup>

      {/* ── Lijn ────────────────────────────────────────────────────────── */}
      <RibbonGroup
        id="line"
        hidden={readOnly}
        label={t("ribbon.groups.line")}
        shortLabel={t("ribbon.groupsShort.line")}
        priority={6}
      >
        <Row>
          <ToolbarButton
            disabled={!magLijnen || !canOrthogonalize}
            onClick={orthogonalize}
            title={t("ribbon.orthogonalize")}
            label="⊿"
          />
          <FormatPainterButton
            workspaceId={workspaceId}
            branchId={branchId}
            selectedNodes={selected}
            selectedEdgeIds={selectedRelationIds}
          />
        </Row>
        <Row>
          {/*
            Routing-dropdown — also lives in AttributePanel for single-edge
            edits, but mirrored here so a bulk-selection of edges can be
            re-routed in one go (the attribute panel only shows attributes
            of one relation at a time).
          */}
          <select
            disabled={!magLijnen || !hasEdgeSelection}
            value={routingValue}
            title={
              multiEdgeSelection
                ? t("ribbon.routing.multi", { n: selectedRelationIds.length })
                : t("ribbon.routing.single")
            }
            onChange={(e) => {
              if (!hasEdgeSelection || !workspaceId || !branchId) return;
              const v = e.target.value as "smoothstep" | "step" | "straight" | "bezier" | "manual";
              const ws = useWorkspaceData.getState();
              for (const id of selectedEdgeKeys) {
                ws.setRelationRoutingOnCanvas(workspaceId, branchId, id, v);
              }
              // UX-koppeling: kies de gebruiker "Recht" voor de path-stijl,
              // dan willen ze meestal ook dat de LIJN visueel recht wordt.
              // Routing alleen zet enkel de bezier/step/etc. stijl — het
              // pad blijft diagonaal als de endpoints diagonaal zijn. Door
              // hier impliciet `orthogonalize()` te draaien wordt de
              // intuïtie "Recht = lijn-recht" wel waar. Alleen bij een
              // single-edge-selection (anders is 't gedrag onverwacht
              // voor multi-select).
              if (v === "straight" && selectedRelationIds.length === 1) {
                orthogonalize();
              }
            }}
            className="h-7 w-[110px] rounded border border-(--ld-border) bg-(--ld-surface) px-1 text-(--ld-text) text-xs disabled:border-(--ld-control) disabled:bg-(--ld-raised) disabled:text-(--ld-muted) disabled:opacity-60 focus:outline-none"
          >
            <option value="smoothstep">Smoothstep</option>
            <option value="step">Step</option>
            <option value="straight">{t("attributePanel.routing.straight")}</option>
            <option value="bezier">Bezier</option>
            <option value="manual">{t("ribbon.routing.manual")}</option>
          </select>
          <select
            // Beschikbaar wanneer er een edge OF een node geselecteerd is —
            // de dropdown raakt zowel relatie-strokes (`lineStyle` op
            // viewState) als node-borders (`nodeStyle.borderStyle`).
            disabled={!hasEdgeSelection && !hasSelection}
            value={lineStyleValue}
            title={
              multiEdgeSelection
                ? t("ribbon.lineStyle.multi", { n: selectedRelationIds.length })
                : hasEdgeSelection
                  ? t("ribbon.lineStyle.single")
                  : selected.length > 1
                    ? t("ribbon.lineStyle.borderMulti", { n: selected.length })
                    : t("ribbon.lineStyle.borderSingle")
            }
            onChange={(e) => {
              if (!workspaceId || !branchId) return;
              const v = e.target.value as "default" | "solid" | "dashed" | "dotted" | "none";
              const next = v === "default" ? null : v;
              const ws = useWorkspaceData.getState();
              // Alle geselecteerde edges krijgen de stroke-pattern,
              // én alle geselecteerde nodes krijgen dezelfde border-pattern.
              // De gebruiker hoeft niet te kiezen welk soort selectie
              // hij heeft — de dropdown werkt op beide tegelijk.
              for (const id of selectedEdgeKeys) {
                ws.setRelationLineStyleOnCanvas(workspaceId, branchId, id, next);
              }
              for (const n of selected) {
                ws.setNodeStyleOnCanvas(workspaceId, branchId, n.id, {
                  borderStyle: next ?? undefined,
                } as Partial<NodeStyle>);
              }
            }}
            className="h-7 w-[110px] rounded border border-(--ld-border) bg-(--ld-surface) px-1 text-(--ld-text) text-xs disabled:border-(--ld-control) disabled:bg-(--ld-raised) disabled:text-(--ld-muted) disabled:opacity-60 focus:outline-none"
          >
            <option value="default">{t("ribbon.default")}</option>
            <option value="solid">{t("ribbon.lineStyle.solid")}</option>
            <option value="dashed">{t("ribbon.lineStyle.dashed")}</option>
            <option value="dotted">{t("ribbon.lineStyle.dotted")}</option>
            <option value="none">{t("ribbon.lineStyle.none")}</option>
          </select>
        </Row>
      </RibbonGroup>
      {/* ── Help ── altijd-enabled knop. Dubbele functie: opent de
          gebruikershandleiding (Cmd/Ctrl+/) én zorgt dat de toolbar-
          scope altijd minstens één tabable heeft (anders kan F7→Toolbar
          geen focus pakken bij geen canvas-selectie). */}
      {/* ── Overlay ── deterministische kleur/dim-regels (conditional
          formatting). Altijd-enabled toggle van het regel-paneel. */}
      <RibbonGroup
        id="overlay"
        hidden={readOnly}
        label={t("ribbon.groups.overlay")}
        shortLabel={t("ribbon.groupsShort.overlay")}
        priority={1}
      >
        <Row>
          <ToolbarButton
            disabled={nietBeschikbaar?.overlay === true}
            onClick={() => setOverlayPanelOpen(!overlayPanelOpen)}
            title={t("overlay.toolbarTitle")}
            label="🎨"
            active={overlayPanelOpen}
          />
          <ToolbarButton
            disabled={nietBeschikbaar?.overlay === true}
            onClick={() => setSimplifyPanelOpen(!simplifyPanelOpen)}
            title={t("simplify.toolbarTitle")}
            label="🔻"
            active={simplifyPanelOpen}
          />
        </Row>
      </RibbonGroup>

      <RibbonGroup id="help" label="?" priority={Number.POSITIVE_INFINITY}>
        <Row>
          <ToolbarButton
            disabled={false}
            onClick={() => window.dispatchEvent(new CustomEvent("ae:toggle-help"))}
            title={t("ribbon.helpTitle")}
            label="?"
            active={false}
            className="!text-base font-bold"
          />
        </Row>
      </RibbonGroup>
    </RibbonBar>
  );
}

/** Single horizontal row inside a ribbon group. */
function Row({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center gap-0.5">{children}</div>;
}

function ToolbarButton({
  disabled,
  onClick,
  title,
  label,
  active,
  className = "",
}: {
  disabled: boolean;
  onClick: () => void;
  title: string;
  /** Text label or any ReactNode (e.g. an inline SVG icon). */
  label: React.ReactNode;
  active?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={active}
      className={`flex h-7 w-7 items-center justify-center rounded text-sm ${
        active
          ? "bg-(--ld-primary) text-(--ld-primary-ink)"
          : "text-(--ld-text) hover:bg-(--ld-active)"
      } disabled:cursor-not-allowed disabled:text-(--ld-muted) disabled:opacity-50 disabled:hover:bg-transparent ${className}`}
    >
      {label}
    </button>
  );
}

/**
 * Format-painter ("toverstaf"). Twee-staps interactie:
 *
 *   1. Selecteer één bron → klik op de wand → opmaak wordt vastgelegd
 *      (knop wordt geel, tooltip wijzigt naar "Selecteer doel-objecten").
 *   2. Selecteer doel(en) → klik nogmaals → de opmaak wordt overgenomen
 *      voor zover het doel die eigenschappen heeft. State keert terug
 *      naar idle.
 *
 * Cross-mapping: een node-rand-kleur wordt op een edge een stroke-kleur
 * en vice versa, zodat de gebruiker echt elk paar visuele objecten kan
 * matchen — een gestreepte rechthoek-rand kopiëren naar een lijn
 * resulteert in een gestreepte lijn met dezelfde kleur.
 */
function FormatPainterButton({
  workspaceId,
  branchId,
  selectedNodes,
  selectedEdgeIds,
}: {
  workspaceId: string | null;
  branchId: string | null;
  selectedNodes: Node[];
  selectedEdgeIds: string[];
}) {
  const { t } = useTranslation();
  const painter = useUi((s) => s.formatPainter);
  const armFormatPainter = useUi((s) => s.armFormatPainter);
  const clearFormatPainter = useUi((s) => s.clearFormatPainter);
  const isArmed = !!painter;
  const hasSource = selectedNodes.length > 0 || selectedEdgeIds.length > 0;

  const onClick = () => {
    if (!workspaceId || !branchId) return;
    const ws = useWorkspaceData.getState();
    const diagramId = ws.activeDiagramId;
    if (!diagramId) return;
    const diagram = ws.store.getDiagram(diagramId);
    if (!diagram) return;

    if (!isArmed) {
      // Stap 1: capture. Eerste selectie wint — node-bron krijgt
      // voorrang boven edge-bron als beide actief zijn (de gebruiker
      // heeft typisch ofwel een node ofwel een edge geselecteerd).
      const sourceNode = selectedNodes[0];
      const sourceEdgeId = selectedEdgeIds[0];
      let captured: CapturedFormat | null = null;
      if (sourceNode) {
        const ve = diagram.viewState[sourceNode.id];
        const ns = ve?.nodeStyle;
        captured = {
          ...(ns?.strokeColor !== undefined ? { strokeColor: ns.strokeColor } : {}),
          ...(ns?.borderStyle !== undefined ? { strokeStyle: ns.borderStyle } : {}),
          ...(ns?.fillColor !== undefined ? { fillColor: ns.fillColor } : {}),
          ...(ns?.fontColor !== undefined ? { fontColor: ns.fontColor } : {}),
          ...(ns?.fontSize !== undefined ? { fontSize: ns.fontSize } : {}),
          ...(ns?.fontFamily !== undefined ? { fontFamily: ns.fontFamily } : {}),
          ...(ns?.bold !== undefined ? { bold: ns.bold } : {}),
          ...(ns?.italic !== undefined ? { italic: ns.italic } : {}),
          ...(ns?.underline !== undefined ? { underline: ns.underline } : {}),
        };
      } else if (sourceEdgeId) {
        const ve = vsGet(diagram.viewState, sourceEdgeId);
        captured = {
          ...(ve?.lineColor !== undefined ? { strokeColor: ve.lineColor } : {}),
          ...(ve?.lineStyle !== undefined ? { strokeStyle: ve.lineStyle } : {}),
          ...(ve?.labelBgColor !== undefined ? { labelBgColor: ve.labelBgColor } : {}),
        };
      }
      if (!captured || Object.keys(captured).length === 0) {
        useWorkspaceData.setState({
          lastError: t("ribbon.formatPainter.noFormat"),
        });
        return;
      }
      armFormatPainter(captured);
      return;
    }

    // Stap 2: apply. Toepassen op iedereen die nu geselecteerd is —
    // ook de oorspronkelijke bron, dat doet niets schadelijks (idempotent).
    const fmt = painter.captured;
    if (!hasSource) {
      // Geen doel geselecteerd → state vrijgeven en niets doen.
      clearFormatPainter();
      return;
    }
    // Nodes: alleen de NodeStyle-velden uit de bag toepassen.
    const nodePatch: NodeStylePatch = {};
    if (fmt.strokeColor !== undefined) nodePatch.strokeColor = fmt.strokeColor;
    if (fmt.strokeStyle !== undefined) nodePatch.borderStyle = fmt.strokeStyle;
    if (fmt.fillColor !== undefined) nodePatch.fillColor = fmt.fillColor;
    if (fmt.fontColor !== undefined) nodePatch.fontColor = fmt.fontColor;
    if (fmt.fontSize !== undefined) nodePatch.fontSize = fmt.fontSize;
    if (fmt.fontFamily !== undefined) nodePatch.fontFamily = fmt.fontFamily;
    if (fmt.bold !== undefined) nodePatch.bold = fmt.bold;
    if (fmt.italic !== undefined) nodePatch.italic = fmt.italic;
    if (fmt.underline !== undefined) nodePatch.underline = fmt.underline;
    for (const n of selectedNodes) {
      ws.setNodeStyleOnCanvas(workspaceId, branchId, n.id, nodePatch as Partial<NodeStyle>);
    }
    // Edges: lineColor / lineStyle / labelBgColor.
    for (const id of selectedEdgeIds) {
      if (fmt.strokeColor !== undefined) {
        ws.setRelationLineColorOnCanvas(workspaceId, branchId, id, fmt.strokeColor);
      }
      if (fmt.strokeStyle !== undefined) {
        ws.setRelationLineStyleOnCanvas(workspaceId, branchId, id, fmt.strokeStyle);
      }
      if (fmt.labelBgColor !== undefined) {
        ws.setRelationLabelBgColorOnCanvas(workspaceId, branchId, id, fmt.labelBgColor);
      }
    }
    clearFormatPainter();
  };

  const tooltip = isArmed ? t("ribbon.formatPainter.armed") : t("ribbon.formatPainter.idle");

  return (
    <ToolbarButton
      disabled={!workspaceId || !branchId || (!isArmed && !hasSource)}
      onClick={onClick}
      title={tooltip}
      active={isArmed}
      label={
        <span aria-hidden className={isArmed ? "text-(--ld-accent)" : ""}>
          🪄
        </span>
      }
    />
  );
}
